import React, { useState, useEffect, useCallback } from 'react';
import { FaChevronDown, FaChevronUp } from 'react-icons/fa';
import { toast } from 'react-toastify';
import { useAppSelector } from '@/redux/Store';
import { ProfileFees } from '@/redux/slices/profileSlice';
import api from '@/utils/api';
import { buildSignBroadcast } from '@/utils/kleverTx';
import './ProfileAccessPanel.scss';

interface ProfileAccessPanelProps {
  /** On-chain numeric profile ID */
  profileId: number;
  profileFees: ProfileFees;
  creatorName: string;
}

interface AccessStatus {
  hasViewAccess: boolean;
  hasSubscription: boolean;
  subscriptionExpiresAt: string | null;
}

type TxStep = 'idle' | 'signing' | 'done' | 'error';

/** Convert a display KLYX amount (e.g. "1.00") to smallest-unit integer string ("100000000"). */
function toSmallestUnit(display: string): string {
  const [whole = '0', frac = ''] = display.split('.');
  const fracPadded = frac.padEnd(8, '0').slice(0, 8);
  return (BigInt(whole) * 100_000_000n + BigInt(fracPadded)).toString();
}

function formatExpiry(isoString: string): string {
  return new Date(isoString).toLocaleDateString('en-US', {
    month: 'short', day: 'numeric', year: 'numeric',
  });
}

function periodLabel(seconds: number): string {
  const days = Math.round(seconds / 86400);
  if (days === 30) return '30 days';
  if (days === 90) return '90 days';
  if (days === 180) return '6 months';
  if (days === 365) return '1 year';
  return `${days} days`;
}

const ProfileAccessPanel: React.FC<ProfileAccessPanelProps> = ({
  profileId,
  profileFees,
  creatorName,
}) => {
  const authUser = useAppSelector((state) => state.auth.user);
  const isEmailUser = authUser?.authMethod === 'email';

  const [status, setStatus] = useState<AccessStatus | null>(null);
  const [statusLoading, setStatusLoading] = useState(true);

  const [isOpen, setIsOpen] = useState(false);
  const [viewStep, setViewStep] = useState<TxStep>('idle');
  const [subStep, setSubStep] = useState<TxStep>('idle');
  const [viewError, setViewError] = useState<string | null>(null);
  const [subError, setSubError] = useState<string | null>(null);

  const hasViewFee = parseFloat(profileFees.viewFee || '0') > 0;
  const hasSubFee = parseFloat(profileFees.subscriptionFee || '0') > 0;

  const fetchStatus = useCallback(async () => {
    setStatusLoading(true);
    try {
      const { data } = await api.get(`/blockchain-profiles/${profileId}/my-access`);
      setStatus(data);
    } catch {
      // non-fatal — panel still shows buttons
      setStatus({ hasViewAccess: false, hasSubscription: false, subscriptionExpiresAt: null });
    } finally {
      setStatusLoading(false);
    }
  }, [profileId]);

  useEffect(() => {
    fetchStatus();
  }, [fetchStatus]);

  const handleBuyViewAccess = async () => {
    setViewError(null);
    setViewStep('signing');
    try {
      let txHash: string;
      if (isEmailUser) {
        const { data } = await api.post('/posts/tx/pay-view-fee-email', {
          profileId,
          fee: profileFees.viewFee,
        });
        txHash = data.txHash;
      } else {
        const { data } = await api.post('/posts/tx/pay-view-fee', {
          profileId,
          fee: profileFees.viewFee,
        });
        txHash = await buildSignBroadcast(data);
        // Wait for server-side confirmation so DB is written before we update UI
        await api.post(`/blockchain-profiles/${profileId}/access/view`, {
          txHash,
          paymentAmount: toSmallestUnit(profileFees.viewFee || '0'),
        });
      }

      setViewStep('done');
      toast.success(`View access purchased! You now have permanent access to ${creatorName}'s paid content.`);
      setStatus(prev =>
        prev
          ? { ...prev, hasViewAccess: true }
          : { hasViewAccess: true, hasSubscription: false, subscriptionExpiresAt: null }
      );
    } catch (err: any) {
      const msg = err?.response?.data?.error || err?.message || 'Transaction failed';
      setViewError(msg);
      toast.error(msg);
      setViewStep('error');
    }
  };

  const handleSubscribe = async () => {
    setSubError(null);
    setSubStep('signing');
    try {
      if (isEmailUser) {
        await api.post(`/blockchain-profiles/${profileId}/subscribe-email`, {
          fee: profileFees.subscriptionFee,
        });
      } else {
        const { data } = await api.post(`/blockchain-profiles/${profileId}/subscribe-build-tx`, {
          fee: profileFees.subscriptionFee,
        });
        const txHash = await buildSignBroadcast(data);
        await api.post(`/blockchain-profiles/${profileId}/subscribe`, {
          txHash,
          paymentAmount: toSmallestUnit(profileFees.subscriptionFee || '0'),
        });
      }
      setSubStep('done');
      const isRenewal = status?.hasSubscription;
      toast.success(isRenewal ? 'Subscription renewed!' : `Subscribed to ${creatorName}!`);
      // Optimistically update — server already verified on-chain before returning
      await fetchStatus();
    } catch (err: any) {
      const msg = err?.response?.data?.error || err?.message || 'Transaction failed';
      setSubError(msg);
      toast.error(msg);
      setSubStep('error');
    }
  };

  if (!hasViewFee && !hasSubFee) return null;

  const viewBusy = viewStep === 'signing';
  const subBusy = subStep === 'signing';

  return (
    <div className="profile-access-panel">
      <button className="access-panel-header" onClick={() => setIsOpen(o => !o)}>
        <h4 className="access-panel-title">Content Access</h4>
        {isOpen ? <FaChevronUp className="access-panel-chevron" /> : <FaChevronDown className="access-panel-chevron" />}
      </button>

      <div className={`access-collapsible-body ${isOpen ? 'access-collapsible-body--open' : ''}`}>
      <div className="access-collapsible-inner">
      {/* View Access */}
      {hasViewFee && (
        <div className="access-row">
          <div className="access-row__info">
            <span className="access-row__icon">🔓</span>
            <div className="access-row__text">
              <span className="access-row__label">One-Time View Access</span>
              <span className="access-row__desc">Permanent access to all paid posts</span>
            </div>
            <span className="access-row__price">
              {parseFloat(profileFees.viewFee).toFixed(2)} KLV
            </span>
          </div>

          {statusLoading ? (
            <div className="access-status access-status--loading">Checking…</div>
          ) : status?.hasViewAccess ? (
            <div className="access-status access-status--granted">
              <span className="access-check">✓</span> Permanent access granted
            </div>
          ) : (
            <>
              {viewError && <p className="access-error">{viewError}</p>}
              <button
                className="btn btn-primary access-btn"
                onClick={handleBuyViewAccess}
                disabled={viewBusy || subBusy}
              >
                {viewBusy
                  ? (isEmailUser ? '⏳ Processing…' : '⏳ Waiting for wallet…')
                  : `Pay ${parseFloat(profileFees.viewFee).toFixed(2)} KLV`}
              </button>
            </>
          )}
        </div>
      )}

      {/* Subscription */}
      {hasSubFee && (
        <div className="access-row access-row--sub">
          <div className="access-row__info">
            <span className="access-row__icon">🔄</span>
            <div className="access-row__text">
              <span className="access-row__label">Subscribe</span>
              <span className="access-row__desc">
                Renews every {periodLabel(profileFees.subscriptionPeriodSeconds || 2592000)}
              </span>
            </div>
            <span className="access-row__price">
              {parseFloat(profileFees.subscriptionFee).toFixed(2)} KLV
            </span>
          </div>

          {statusLoading ? (
            <div className="access-status access-status--loading">Checking…</div>
          ) : status?.hasSubscription ? (
            <div className="access-status--subscribed">
              <div className="access-status access-status--granted">
                <span className="access-check">✓</span>
                Active until {status.subscriptionExpiresAt ? formatExpiry(status.subscriptionExpiresAt) : '—'}
              </div>
              {subError && <p className="access-error">{subError}</p>}
              <button
                className="btn btn-secondary access-btn access-btn--renew"
                onClick={handleSubscribe}
                disabled={subBusy || viewBusy}
              >
                {subBusy
                  ? (isEmailUser ? '⏳ Processing…' : '⏳ Waiting for wallet…')
                  : 'Renew early'}
              </button>
            </div>
          ) : (
            <>
              {subError && <p className="access-error">{subError}</p>}
              <button
                className="btn btn-secondary access-btn"
                onClick={handleSubscribe}
                disabled={subBusy || viewBusy}
              >
                {subBusy
                  ? (isEmailUser ? '⏳ Processing…' : '⏳ Waiting for wallet…')
                  : `Subscribe for ${parseFloat(profileFees.subscriptionFee).toFixed(2)} KLV`}
              </button>
            </>
          )}
        </div>
      )}
      </div>
      </div>
    </div>
  );
};

export default ProfileAccessPanel;
